import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  PERFORMANCE_DASHBOARD_DAILY_PERIOD_OPTIONS,
  PERFORMANCE_DASHBOARD_MONTHLY_PERIOD_OPTIONS,
  PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS,
  PERFORMANCE_DASHBOARD_TIME_PERIOD_DATA,
  PERFORMANCE_DASHBOARD_TIME_PERIODS,
  PERFORMANCE_DASHBOARD_WEEKLY_PERIOD_OPTIONS,
} from "shared/lib/constants/performance";
import {
  UserPerformanceFilterI,
  PerformanceMetricI,
  PerformanceMetricsI,
  PerformanceTimeFrequencyI,
  PerformanceTimePeriodI,
} from "shared/lib/interfaces/performance";
import { calculatePercentage } from "shared/lib/helpers/numbers";
import { TimeUnitI } from "shared/lib/interfaces/time";

dayjs.extend(utc);

export const preparePerformanceFiltersForAPICall = ({
  selectedCampaignIds,
  timePeriod,
}: UserPerformanceFilterI) => {
  const startDateConfigMap = {
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.SEVEN_DAYS]: () =>
      dayjs().subtract(7, "day").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.FOURTEEN_DAYS]: () =>
      dayjs().subtract(14, "day").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.THIRTY_DAYS]: () =>
      dayjs().subtract(30, "day").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.SIXTY_DAYS]: () =>
      dayjs().subtract(60, "day").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.FOUR_WEEKS]: () =>
      dayjs().subtract(4, "week").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.EIGHT_WEEKS]: () =>
      dayjs().subtract(8, "week").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.TWELVE_WEEKS]: () =>
      dayjs().subtract(12, "week").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.TWENTY_FOUR_WEEKS]: () =>
      dayjs().subtract(24, "week").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.THREE_MONTHS]: () =>
      dayjs().subtract(3, "month").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.SIX_MONTHS]: () =>
      dayjs().subtract(6, "month").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.NINE_MONTHS]: () =>
      dayjs().subtract(9, "month").startOf("day"),
    [PERFORMANCE_DASHBOARD_TIME_PERIODS.TWELVE_MONTHS]: () =>
      dayjs().subtract(12, "month").startOf("day"),
  };

  return {
    campaignId: selectedCampaignIds?.[0],
    startDate: Object.hasOwn(startDateConfigMap, timePeriod)
      ? startDateConfigMap[timePeriod]().toISOString()
      : dayjs().startOf("day").toISOString(),
    endDate: undefined,
  };
};

export const getStartDateOfTimePeriod = (
  timePeriod: PerformanceTimePeriodI
) => {
  const numDaysToGoBackBy =
    PERFORMANCE_DASHBOARD_TIME_PERIOD_DATA[timePeriod].days;
  const startDate = dayjs().subtract(numDaysToGoBackBy, "day").startOf("day");

  return startDate.toISOString();
};

export const createPerformanceDashboardMetrics = ({
  calls = 0,
  conversations = 0,
  booked_meetings = 0,
  run_meetings = 0,
  showed_meetings = 0,
  qualified_meetings = 0,
}: PerformanceMetricsI): PerformanceMetricI[] => [
  {
    percentage: calculatePercentage(conversations, calls) || "N/A",
    fraction: `${conversations}/${calls}`,
    label: "Conversations",
  },
  {
    percentage: calculatePercentage(booked_meetings, conversations) || "N/A",
    fraction: `${booked_meetings}/${conversations}`,
    label: "Bookings",
  },
  {
    percentage: calculatePercentage(showed_meetings, run_meetings) || "N/A",
    fraction: `${showed_meetings}/${run_meetings}`,
    label: "Show Up Rate",
  },
  {
    percentage:
      calculatePercentage(qualified_meetings, showed_meetings) || "N/A",
    fraction: `${qualified_meetings}/${showed_meetings}`,
    label: "Qualified Meetings",
  },
];

// converts a given number of days to a new time unit (days, weeks, months).
export const convertNumDaysToTimeUnit = (
  numDays: number,
  timeUnit: TimeUnitI
) => {
  switch (timeUnit) {
    case "day":
      return numDays;
    case "week":
      return numDays / 7;
    case "month":
      return numDays / 30;
  }
};

type DataItemToAggregateI<T> = {
  start_time: string;
  end_time: string;
  value: T;
  meta?: Record<string, any>;
};

type ChildDataToAggregateI<T> = DataItemToAggregateI<T>[][];

const createDataItemFilter =
  (rangeStart: Dayjs) =>
  <T>(item: DataItemToAggregateI<T>) => {
    const startTime = dayjs(item.start_time, "YYYY-MM-DDTHH:mm:ssZ").utc();

    return startTime.isAfter(rangeStart) || startTime.isSame(rangeStart, "day");
  };

const createPeriodDataFilter =
  (
    timeUnit: TimeUnitI,
    periodStart: Dayjs,
    periodEnd: Dayjs,
    currentStart: Dayjs
  ) =>
  <T>(item: DataItemToAggregateI<T>) => {
    const startTime = dayjs(item.start_time, "YYYY-MM-DDTHH:mm:ssZ").utc();

    return (
      (startTime.isAfter(currentStart) ||
        startTime.isSame(currentStart, timeUnit)) &&
      (startTime.isBefore(periodEnd) || startTime.isSame(periodEnd, timeUnit))
    );
  };

/**
 * Trims the data to only include the data for the specified time period (ex. "daily, last 7 days", "daily,last 30 days", "weekly, last 4 weeks", etc.).
 * Also creates labels showing the date range of each data item (useful for graph axis labels).
 * @param data - The data to process. Must have `start_time` and `end_time` properties.
 * @param childData - The child data to process. Must have `start_time` and `end_time` properties.
 * @param timeUnit - "day", "week", or "month" that the data is grouped by.
 * @param numTimeUnits - The number of time units to include in the output (ex. if timeUnit is "day" and numTimeUnits is 7, the output will include data for the past 7 days).
 * @param aggregateFn - A function that aggregates the data for each time period.
 * @returns The data with a label for each group that shows the start and end dates of the group (useful for graph axis labels).
 */
export const trimPerformanceDataToTimePeriod = <T>(
  data: DataItemToAggregateI<T>[],
  childData: ChildDataToAggregateI<T>,
  timeUnit: TimeUnitI,
  numTimeUnits: number,
  aggregateFn: (
    periodData: { start_time: string; end_time: string; value: T }[]
  ) => T
) => {
  if (!data || data.length === 0) {
    return { result: [] };
  }

  const currentDate = dayjs().utc();

  // the start of the time period we will include in the output
  const rangeStart = currentDate
    .subtract(numTimeUnits, timeUnit)
    .startOf("day")
    .utc();

  // Create and use a single filter of data items for both top level metric and
  // child metrics.
  const filterDataItem = createDataItemFilter(rangeStart);

  // Only include entries with a start_time within the specified time period
  const filteredData = data.filter(filterDataItem);

  // Only include entries with a start_time within the specified time period
  const filteredChildData: ChildDataToAggregateI<T> = [];

  for (const child of childData) {
    const filteredChild = child.filter(filterDataItem);

    filteredChildData.push(filteredChild);
  }

  // result array to store the processed data
  const result = [];
  const childResult = Array.from(
    { length: childData.length },
    () => [] as Array<{ label: string; totalValue: T; meta?: any }>
  );

  let currentStart = rangeStart;

  // loop through each time period in the time range, grouping the data that falls into that time period along the way
  while (
    currentStart.isBefore(currentDate) ||
    currentStart.isSame(currentDate)
  ) {
    // Define the end of the current period (based on the time unit and number of units)
    let periodEnd: dayjs.Dayjs;

    // determine the end of the period based on the time unit.
    // We need the endOf() method to get the last calendar day of the period (instead of just incrementing the date by 1 unit size because that could land somewhere in the middle of the week or month).
    switch (timeUnit) {
      case "day":
        periodEnd = currentStart.endOf("day").utc();
        break;
      case "week":
        periodEnd = currentStart.endOf("week").utc();
        break;
      case "month":
        periodEnd = currentStart.endOf("month").utc();
        break;
      default:
        periodEnd = currentStart.add(1, timeUnit).subtract(1, "second").utc();
        break;
    }

    const filterPeriodData = createPeriodDataFilter(
      timeUnit,
      currentStart,
      periodEnd,
      currentStart
    );

    // Filter data for the current group
    const periodData = filteredData.filter(filterPeriodData);

    const childPeriodData = filteredChildData.map((child) =>
      child.filter(filterPeriodData)
    );

    // Sum the values of this time period's data
    const totalValue = aggregateFn(periodData);

    // Create the label for the time period (e.g. "Oct 28", or "Oct 28 - Nov 3")
    let label = "";
    switch (timeUnit) {
      case "day":
        label = currentStart.format("MMM D");
        break;
      case "week": {
        const labelStart = currentStart.format("MMM D");
        const labelEnd = periodEnd.isAfter(currentDate)
          ? currentDate.format("MMM D")
          : periodEnd.format("MMM D");
        label = `${labelStart} - ${labelEnd}`;
        break;
      }
      case "month":
        label = currentStart.format("MMM YYYY");
        break;
    }

    // Add the data to the result
    result.push({
      label,
      totalValue,
    });

    childResult.forEach((child, index) => {
      const childTotalValue = aggregateFn(childPeriodData[index]);

      child.push({
        label,
        totalValue: childTotalValue,
      });
    });

    // Move to the next group
    currentStart = periodEnd.add(1, "second").utc();

    switch (timeUnit) {
      case "week":
        currentStart = currentStart.startOf("week").utc();
        break;
      case "month":
        currentStart = currentStart.startOf("month").utc();
        break;
    }
  }

  return { result, childResult };
};

/**
 * Some API calls now take only a single campaignId string, while newer APIs take an array of campaignIds.
 * This helper function handles the transition between the two so that older APIs can still be used.
 * If ONLY 1 campaignId is not selected, it will return undefined because we don't want to render the component.
 * @param selectedCampaignIds - The array of campaignIds to process.
 * @param blockedCampaignIds - The array of campaignIds that will be excluded from the output.
 * @returns The first campaignId from the selectedCampaignIds array if it exists, otherwise undefined.
 */
export const getSingleCampaignIdFromSelectedCampaignIds = (
  selectedCampaignIds?: string[],
  blockedCampaignIds?: string[]
) => {
  {
    if (selectedCampaignIds?.length && selectedCampaignIds.length === 1) {
      if (blockedCampaignIds?.includes(selectedCampaignIds[0])) {
        return undefined;
      }

      return selectedCampaignIds[0];
    }

    return undefined;
  }
};

export const getDefaultTimePeriodForFrequency = (
  timeFrequency: PerformanceTimeFrequencyI
) => {
  const FREQUENCY_TO_DEFAULT_TIME_PERIOD_MAP = {
    [PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS.DAILY]:
      PERFORMANCE_DASHBOARD_DAILY_PERIOD_OPTIONS.FOURTEEN_DAYS,
    [PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS.WEEKLY]:
      PERFORMANCE_DASHBOARD_WEEKLY_PERIOD_OPTIONS.FOUR_WEEKS,
    [PERFORMANCE_DASHBOARD_TIME_FREQUENCY_OPTIONS.MONTHLY]:
      PERFORMANCE_DASHBOARD_MONTHLY_PERIOD_OPTIONS.THREE_MONTHS,
  };

  return FREQUENCY_TO_DEFAULT_TIME_PERIOD_MAP[timeFrequency];
};
